import { useState } from 'react';
import { Button } from './ui/button';
import { 
  MessageSquare, Sparkles, Loader2, X, Send,
  TrendingUp, TrendingDown, AlertTriangle, Lightbulb, Rocket
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { LaunchCampaignButton } from './LaunchCampaignButton';

import { API_BASE_URL as API } from '../utils/apiConfig';

/**
 * AskAIButton - A reusable component that adds AI explanation capability to any chart/insight
 * 
 * Usage:
 * <AskAIButton 
 *   context="Repeat Ratio is 42%, target is >40%"
 *   chartTitle="Repeat Ratio KPI"
 *   chartData={[{month: 'Jan', value: 40}, ...]}
 *   brandId="20001"
 * />
 */
export function AskAIButton({ 
  context, 
  chartTitle, 
  chartData,
  brandId,
  dashboardType = 'command_center',
  size = 'sm',
  variant = 'ghost',
  className = ''
}) {
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Initializing...');
  
  const handleAskAI = async () => {
    if (!brandId) {
      toast.error('Brand context missing — select a brand first to use Ask AI.');
      return;
    }
    setShowPopup(true);
    setLoading(true);
    setAiResponse(null);
    setProgress(0);
    setProgressText('Connecting to AI...');
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        const increment = prev < 30 ? 8 : prev < 60 ? 5 : prev < 80 ? 3 : 1;
        return prev + increment;
      });
    }, 300);
    
    // Update progress text
    setTimeout(() => setProgressText('Analyzing patterns...'), 1500);
    setTimeout(() => setProgressText('Generating insights...'), 3500);
    setTimeout(() => setProgressText('Preparing recommendations...'), 6000);
    
    try {
      // Call the chart insights API for quick analysis with timeout
      const response = await axios.post(
        `${API}/api/analytics/chart-insights`,
        {
          brand_id: brandId,
          chart_type: 'metric',
          chart_title: chartTitle,
          chart_data: chartData || [{ name: chartTitle, value: context }],
          dashboard_type: dashboardType,
          use_ai: true,
          context: context
        },
        { 
          withCredentials: true,
          timeout: 45000  // 45 second timeout
        }
      );
      
      clearInterval(progressInterval);
      setProgress(100);
      setProgressText('Complete!');
      
      if (response.data.success) {
        setAiResponse(response.data.insights);
      } else {
        toast.error(response.data.error || 'Failed to get AI analysis');
        setAiResponse(null);
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Ask AI error:', err);
      
      if (err.code === 'ECONNABORTED') {
        toast.error('Analysis timed out. Please try again.');
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please refresh the page.');
      } else {
        toast.error('Failed to get AI analysis. Please try again.');
      }
      setAiResponse(null);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={`gap-1 ${className}`}
        onClick={handleAskAI}
        data-testid="ask-ai-btn"
      >
        <Sparkles className="w-3 h-3" />
        <span className="hidden sm:inline">Ask AI</span>
      </Button>
      
      {/* AI Response Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b flex flex-row items-center justify-between py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-500" />
                AI Analysis: {chartTitle}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowPopup(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-violet-500 mb-4" />
                  <p className="text-muted-foreground mb-4">{progressText}</p>
                  <div className="w-full max-w-xs">
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-2">{progress}%</p>
                  </div>
                </div>
              ) : aiResponse ? (
                <div className="space-y-4">
                  {/* Description */}
                  {aiResponse.description && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <h4 className="text-sm font-semibold mb-1">{aiResponse.description.title}</h4>
                      <p className="text-sm text-muted-foreground">{aiResponse.description.content}</p>
                    </div>
                  )}
                  
                  {/* Key Finding */}
                  {aiResponse.key_finding && (
                    <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-violet-500 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-violet-600 dark:text-violet-400">Key Finding</h4>
                          <p className="text-sm">{aiResponse.key_finding}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Trend Analysis */}
                  {aiResponse.analysis && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2 mb-2">
                        {aiResponse.analysis.trend === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : aiResponse.analysis.trend === 'down' ? (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        ) : (
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                        )}
                        <h4 className="text-sm font-semibold">{aiResponse.analysis.title}</h4>
                      </div>
                      <p className="text-sm text-muted-foreground">{aiResponse.analysis.content}</p>
                    </div>
                  )}
                  
                  {/* Key Insights */}
                  {aiResponse.insights?.points?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">Insights</h4>
                      {aiResponse.insights.points.map((point, idx) => (
                        <div 
                          key={idx} 
                          className={`p-2 rounded-lg text-sm flex items-start gap-2 ${
                            point.type === 'positive' ? 'bg-green-500/10' :
                            point.type === 'negative' ? 'bg-red-500/10' :
                            point.type === 'warning' ? 'bg-amber-500/10' : 'bg-blue-500/10'
                          }`}
                        >
                          {point.type === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                          <span>{point.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Recommendations */}
                  {aiResponse.recommendations?.actions?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">Recommended Actions</h4>
                      {aiResponse.recommendations.actions.slice(0, 3).map((action, idx) => (
                        <div key={idx} className="p-2 rounded-lg border text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                              action.priority === 'P0' ? 'bg-red-500 text-white' :
                              action.priority === 'P1' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
                            }`}>
                              {action.priority}
                            </span>
                            <span className="font-medium">{action.action}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <span>Owner: {action.owner}</span>
                              <span>Impact: {action.expected_impact || action.impact}</span>
                            </div>
                            <LaunchCampaignButton
                              brandId={brandId}
                              segmentName={`${chartTitle} - ${action.action?.substring(0, 30) || 'AI Recommendation'}`}
                              recommendation={{
                                action: action.action,
                                priority: action.priority,
                                impact: action.expected_impact || action.impact
                              }}
                              variant="outline"
                              size="xs"
                              className="h-6 px-2 text-[10px] border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* AI Powered Badge */}
                  {aiResponse.ai_powered && (
                    <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
                      <Sparkles className="w-3 h-3 text-violet-500" />
                      AI-Powered Analysis • Confidence: {((aiResponse.confidence_score || 0.8) * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No analysis available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

/**
 * AskAIOverlay - Floating "Ask AI" button for charts
 * Position this absolutely in the chart container
 */
export function AskAIOverlay({ 
  context, 
  chartTitle, 
  chartData,
  brandId,
  dashboardType,
  position = 'top-right'
}) {
  const positionClasses = {
    'top-right': 'top-2 right-2',
    'top-left': 'top-2 left-2',
    'bottom-right': 'bottom-2 right-2',
    'bottom-left': 'bottom-2 left-2'
  };
  
  return (
    <div className={`absolute ${positionClasses[position]} z-10`}>
      <AskAIButton
        context={context}
        chartTitle={chartTitle}
        chartData={chartData}
        brandId={brandId}
        dashboardType={dashboardType}
        size="sm"
        variant="secondary"
        className="shadow-md bg-background/80 backdrop-blur-sm hover:bg-background"
      />
    </div>
  );
}

/**
 * ChartWithAI - Wrapper component that adds AI capability to any chart
 */
export function ChartWithAI({ 
  children, 
  title, 
  data,
  brandId,
  dashboardType,
  context,
  className = ''
}) {
  return (
    <div className={`relative ${className}`}>
      <AskAIOverlay
        context={context || `Chart: ${title}`}
        chartTitle={title}
        chartData={data}
        brandId={brandId}
        dashboardType={dashboardType}
      />
      {children}
    </div>
  );
}

export default AskAIButton;
